import { lazy, useEffect, useState, type PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import type { State } from 'src/types';

const importHbGlobalNavigatorMock = () => import('./HbGlobalNavigatorMock');

// Fired as soon as this module evaluates (part of App.tsx's static import
// chain, so this runs at bundle load, well before auth/session resolves) —
// not when React first tries to render it. Without this, the dynamic import
// below only starts on first render, racing the page content it shifts:
// content has no async import to wait on, so on a hard reload it flashes in
// unpositioned before the nav connects and applies its margin.
const preloadHbGlobalNavigatorMock = import.meta.env.DEV
  ? importHbGlobalNavigatorMock()
  : null;

// import.meta.env.DEV is statically replaced at build time, so bundlers can
// tree-shake this dynamic import (and HbGlobalNavigatorMock itself) out of
// production entirely — see VAULT-20021. Isolated in its own file so
// ReshubNavbarAtlas.tsx stays free of import.meta, which Jest's CommonJS
// transform can't parse.
const HbGlobalNavigatorMockLazy = import.meta.env.DEV
  ? lazy(() =>
      (preloadHbGlobalNavigatorMock ?? importHbGlobalNavigatorMock()).then(
        (m) => ({
          default: m.HbGlobalNavigatorMock,
        }),
      ),
    )
  : null;

// Derive the org-scoped CDN base URL from d1pHomeUrl so the OIDC BFF flow
// resolves the correct home-api Lambda authorizer.
//
// buildD1PHomeUrl (backend/src/authentication/constants.ts) produces:
//   https://<slug>.home.<base-tld>/          (US — no region suffix)
//   https://<slug>.home-uk.<base-tld>/       (non-US — region suffix on "home" segment)
//
// The second subdomain ("home" or "home-uk") carries the region, not the first
// (which is the org slug and may itself contain hyphens). The CDN host uses the
// base TLD directly — no "home" segment:
//   https://<slug>.web-components.<base-tld>/global-navigator/index.js
//
// Examples:
//   https://uitest.home.diligentoneplatform-dev.com/
//     → region: "us",  tld: "diligentoneplatform-dev.com"
//   https://uitest.home-uk.diligentoneplatform-dev.com/
//     → region: "uk",  tld: "diligentoneplatform-dev.com"
function parseD1pHomeUrl(d1pHomeUrl: string): {
  tld: string;
  region: string;
} | null {
  try {
    const { hostname } = new URL(d1pHomeUrl);
    const parts = hostname.split('.');
    // Need at least <slug>.<home-segment>.<tld-part>
    if (parts.length < 3) return null;
    // CDN host is <slug>.web-components.<base-tld> — drop the "home[-region]" segment
    const tld = parts.slice(2).join('.');
    const homeSegment = parts[1]; // "home" or "home-uk"
    // indexOf is correct — REGION_COUNTRY_MAP codes (uk, eu, au, ca…) are
    // hyphen-free, so the first hyphen in "home-uk" is the separator.
    const hyphen = homeSegment.indexOf('-');
    const region = hyphen === -1 ? 'us' : homeSegment.slice(hyphen + 1);
    return { tld, region };
  } catch {
    return null;
  }
}

// No local <Suspense> here — the lazy mock's promise must resolve before
// the *page content* sibling commits too (not just this nav), or content
// flashes in unpositioned while the nav is still loading. The caller wraps
// this together with that content in one shared Suspense boundary.
export const HbGlobalNavigatorShell = ({ children }: PropsWithChildren) => {
  const organisation = useSelector(
    (state: State) => state.organisation.organisation,
  );

  const slug = organisation?.slug;
  const d1pHomeUrl = organisation?.d1pHomeUrl;

  const parsed = d1pHomeUrl ? parseD1pHomeUrl(d1pHomeUrl) : null;
  if (d1pHomeUrl && !parsed) {
    console.warn(
      `HbGlobalNavigatorShell: could not parse d1pHomeUrl "${d1pHomeUrl}" — hb-global-navigator will not be loaded.`,
    );
  }
  const orgScope = parsed && slug ? `${slug}-${parsed.region}` : undefined;
  const orgScopedScriptSrc = (() => {
    if (!parsed || !slug) return undefined;
    let override: string | null = null;
    try {
      override = localStorage.getItem('webComponentsBaseUrl');
    } catch {
      // storage blocked
    }
    const base = override ?? `https://${slug}.web-components.${parsed.tld}`;
    return `${base}/global-navigator/index.js`;
  })();

  // True once hb-global-navigator is registered. Starts true if the element
  // is already defined (e.g. soft navigation back to this page).
  const [isElementDefined, setIsElementDefined] = useState(
    () => !!customElements.get('hb-global-navigator'),
  );

  // Inject the org-scoped script after session resolves. This is the only
  // script that loads hb-global-navigator — no generic fallback in index.html.
  // Loading from the org-scoped CDN URL is required for the OIDC BFF flow:
  // the component derives its home-api base from the origin it was loaded from,
  // so it must be loaded from <slug>.web-components.<tld>, not the generic
  // web-components.<tld>. Skip if already injected (effect firing twice).
  useEffect(() => {
    if (!orgScopedScriptSrc) return;
    if (!document.querySelector(`script[src="${orgScopedScriptSrc}"]`)) {
      const s = document.createElement('script');
      s.src = orgScopedScriptSrc;
      s.async = true;
      // On CDN failure (401/403/unreachable) whenDefined never resolves.
      // Flip the gate so children render un-upgraded rather than vanishing.
      s.onerror = () => setIsElementDefined(true);
      document.head.appendChild(s);
    }
    // Gate rendering until the element is registered so nav children don't
    // flow as unstyled block content before the shadow DOM is ready.
    customElements.whenDefined('hb-global-navigator').then(() => {
      setIsElementDefined(true);
    });
  }, [orgScopedScriptSrc]);

  if (import.meta.env.DEV && HbGlobalNavigatorMockLazy) {
    return <HbGlobalNavigatorMockLazy>{children}</HbGlobalNavigatorMockLazy>;
  }

  if (!isElementDefined) return null;

  return (
    <hb-global-navigator
      app-id="resolution-hub"
      app-origin={window.location.origin}
      enable-app-nav="true"
      display-mode="dynamic"
      enable-oidc="true"
      {...(orgScope ? { 'org-scope': orgScope } : {})}
    >
      {children}
    </hb-global-navigator>
  );
};
